'use client';

import { JSX, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { Slider, Tooltip } from '@videoblocks/design-system';
import { VolumeMedium, VolumeOff } from '@videoblocks/react-icons';

interface AudioPlayerVolumeControlProps {
    className?: string;
    currentVolume: number;
    onVolumeChange: (volume: number) => void;
}

export const AudioPlayerVolumeControl = ({
    className,
    currentVolume,
    onVolumeChange,
}: AudioPlayerVolumeControlProps): JSX.Element => {
    const [prevVolume, setPrevVolume] = useState<number | null>(null);
    const [showTooltip, setShowTooltip] = useState(false);

    const toggleMute = () => {
        if (currentVolume === 0) {
            // unmute the volume
            if (prevVolume !== null) {
                onVolumeChange(prevVolume);
            } else {
                // Default to full volume if muted by default
                onVolumeChange(1);
            }
            setPrevVolume(0);
        } else {
            // Mute the volume
            setPrevVolume(currentVolume);
            onVolumeChange(0);
        }
    };

    const updateVolume = (newVolume: number[]) => {
        const volume = newVolume[0];
        setPrevVolume(volume);
        onVolumeChange(volume);
    };

    const getVolumeIcon = () => {
        return currentVolume === 0 ? (
            <VolumeOff className={'fill-current w-6 h-6'} />
        ) : (
            <VolumeMedium className={'fill-current w-6 h-6'} />
        );
    };
    const isMuted = currentVolume === 0;

    return (
        <div className={twMerge(`flex h-full w-full items-center gap-x-2`, className)}>
            <Tooltip
                open={showTooltip}
                onOpenChange={() => setShowTooltip(!showTooltip)}
                title={isMuted ? 'Unmute' : 'Mute'}
                trigger={
                    <button
                        className="flex text-white cursor-pointer"
                        title={isMuted ? 'Unmute volume' : 'Mute volume'}
                        onClick={toggleMute}
                        onFocus={() => setShowTooltip(true)}
                        onMouseEnter={() => setShowTooltip(true)}
                        onBlur={() => setShowTooltip(false)}
                        onMouseLeave={() => setShowTooltip(false)}
                    >
                        {getVolumeIcon()}
                    </button>
                }
            />
            <Slider
                min={0}
                max={1}
                step={0.01}
                defaultValue={[1]}
                value={[currentVolume]}
                onValueChange={updateVolume}
                variant="light"
            />
        </div>
    );
};
